import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL, ICategoryAllowedServiceDto, ICategoryDto, IServicePlanDto } from '@/dtos';
import {
  PayoutDefinition,
  PayoutDefinitionByIdResponse,
} from '@/modules/ProcessingDefinition/ProcessingDefinition.types';

export const QUERY_PAYOUT_DEFINITION_BY_ID = 'GET_PAYOUT_DEFINITION_BY_ID';

const useGetPayoutDefinitionByIdQuery = (id?: string) => {
  const { data, isFetched, refetch, isLoading, isSuccess, isError } = useQuery(
    [QUERY_PAYOUT_DEFINITION_BY_ID, id],
    () => api.get<PayoutDefinitionByIdResponse>(API_URL.PAYOUT_DEFINITIONS_BY_ID(id || '')),
    {
      enabled: !!id,
    }
  );

  const formattedData = useMemo(() => {
    if (!data) return undefined;

    const responseData = data?.data;

    return {
      id: toString(responseData?.id),
      name: responseData?.name as string,
      isLimited: responseData?.is_limited_use ?? false,
      canBeLimited: responseData?.can_be_limited ?? true,
      mpTableId: toString(responseData?.mp_table_id),
      covTableId: toString(responseData?.cov_table_id),
      organizationPath: responseData?.organization_path,
      categoryTypes: responseData?.categories?.map((category: ICategoryDto) => ({
        categoryType: category?.category_type,
        services: category?.services?.map((service: ICategoryAllowedServiceDto) => ({
          id: toString(service.id),
          name: service.name,
          sequenceId: service.sequence_id,
          plans: service?.plans
            ?.sort((a, b) => (a?.order || 0) - (b?.order || 0))
            ?.map((plan: IServicePlanDto) => ({
              id: toString(plan.plan_id),
              name: plan.name,
              accountType: plan.account_type,
              order: plan.order,
            })),
        })),
      })),
    } as PayoutDefinition;
  }, [data]);

  return {
    formattedData,
    isFetched,
    refetch,
    isLoading,
    isSuccess,
    isError,
  };
};

export default useGetPayoutDefinitionByIdQuery;
